import { useState } from 'react';
import { useAccount, useWalletClient } from 'wagmi';
import { Contract, parseEther, formatEther, keccak256, toUtf8Bytes } from 'ethers';
import { BrowserProvider } from 'ethers';
import { CONTRACT_ADDRESS } from '../contracts/contractAddress';
import BookOfSecretsABI from '../contracts/BookOfSecrets.json';

export interface PageData {
  id: number;
  owner: string;
  encryptedContent: string;
  unlockPrice: string;
  hasPassword: boolean;
  createdAt: number;
  totalUnlocks: number;
  hasAccess: boolean;
  decryptedContent?: string;
}

export function useContract() {
  const { address, isConnected } = useAccount();
  const { data: walletClient } = useWalletClient();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getContract = async () => {
    if (!walletClient || !isConnected) {
      throw new Error('Wallet not connected');
    }

    const provider = new BrowserProvider(walletClient as any);
    const signer = await provider.getSigner();
    return new Contract(CONTRACT_ADDRESS, BookOfSecretsABI.abi, signer);
  };

  const createPage = async (
    secretText: string,
    unlockPriceCBTC: string,
    password: string
  ): Promise<number | null> => {
    if (!isConnected || !address) {
      setError('Wallet not connected');
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const contract = await getContract();

      // Store content as plaintext (access control via payment/password)
      // This allows anyone who unlocks to read the content
      const content = secretText;

      // Prepare unlock parameters
      const unlockPrice = unlockPriceCBTC ? parseEther(unlockPriceCBTC) : 0n;
      const passwordHash = password
        ? keccak256(toUtf8Bytes(password))
        : '0x0000000000000000000000000000000000000000000000000000000000000000';

      // Call contract
      const tx = await contract.createPage(content, unlockPrice, passwordHash);
      const receipt = await tx.wait();

      // Get page ID from event
      const event = receipt.logs.find((log: any) => {
        try {
          return contract.interface.parseLog(log)?.name === 'PageCreated';
        } catch {
          return false;
        }
      });

      const pageId = event ? Number(contract.interface.parseLog(event)?.args.pageId) : 0;

      return pageId;
    } catch (err: any) {
      console.error('Failed to create page:', err);
      setError(err.message || 'Failed to create page');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const unlockWithPayment = async (pageId: number, price: string): Promise<boolean> => {
    if (!isConnected) {
      setError('Wallet not connected');
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      const contract = await getContract();
      const tx = await contract.unlockWithPayment(pageId, {
        value: parseEther(price),
      });
      await tx.wait();
      return true;
    } catch (err: any) {
      console.error('Failed to unlock with payment:', err);
      setError(err.message || 'Failed to unlock page');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const unlockWithPassword = async (pageId: number, password: string): Promise<boolean> => {
    if (!isConnected) {
      setError('Wallet not connected');
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      const contract = await getContract();
      const tx = await contract.unlockWithPassword(pageId, password);
      await tx.wait();
      return true;
    } catch (err: any) {
      console.error('Failed to unlock with password:', err);
      setError(err.message || 'Incorrect password');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const getPage = async (pageId: number): Promise<PageData | null> => {
    if (!isConnected) {
      setError('Wallet not connected');
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const contract = await getContract();
      const page = await contract.getPage(pageId);

      const pageData: PageData = {
        id: Number(page.id),
        owner: page.owner,
        encryptedContent: page.encryptedContent,
        unlockPrice: formatEther(page.unlockPrice),
        hasPassword: page.hasPassword,
        createdAt: Number(page.createdAt),
        totalUnlocks: Number(page.totalUnlocks),
        hasAccess: page.hasAccess,
      };

      // If user has access, show the content
      if (page.hasAccess && page.encryptedContent) {
        // Check if content is old encrypted format (starts with "U2FsdGVkX1")
        if (page.encryptedContent.startsWith('U2FsdGVkX1')) {
          // This is an old encrypted page - try to decrypt if we have the key
          if (address) {
            let encryptionKey = localStorage.getItem(`${address}_page_${pageId}_key`);
            if (!encryptionKey) {
              encryptionKey = localStorage.getItem(`page_${pageId}_key`);
            }

            if (encryptionKey) {
              try {
                // Import decryption function
                const { decryptText } = await import('../utils/encryption');
                pageData.decryptedContent = decryptText(page.encryptedContent, encryptionKey);
              } catch (decryptErr) {
                console.error('Failed to decrypt old content:', decryptErr);
                pageData.decryptedContent = '⚠️ This is an old encrypted page. The encryption key is not available. Only the page creator on their original device can read this content. Please ask them to create a new page with the updated system.';
              }
            } else {
              pageData.decryptedContent = '⚠️ This is an old encrypted page. The encryption key is only available to the creator on their original device. Please ask them to create a new page.';
            }
          } else {
            pageData.decryptedContent = '⚠️ This is an old encrypted page. Only the creator can read this content.';
          }
        } else {
          // New plaintext content
          pageData.decryptedContent = page.encryptedContent;
        }
      }

      return pageData;
    } catch (err: any) {
      console.error('Failed to get page:', err);
      setError(err.message || 'Failed to fetch page');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const getUserPages = async (userAddress?: string): Promise<number[]> => {
    if (!isConnected) {
      setError('Wallet not connected');
      return [];
    }

    setLoading(true);
    setError(null);

    try {
      const contract = await getContract();
      const targetAddress = userAddress || address;
      if (!targetAddress) return [];

      const pageIds = await contract.getUserPages(targetAddress);
      return pageIds.map((id: bigint) => Number(id));
    } catch (err: any) {
      console.error('Failed to get user pages:', err);
      setError(err.message || 'Failed to fetch user pages');
      return [];
    } finally {
      setLoading(false);
    }
  };

  const getPageInfo = async (pageId: number) => {
    if (!isConnected) return null;

    try {
      const contract = await getContract();
      const info = await contract.getPageInfo(pageId);
      return {
        id: Number(info.id),
        owner: info.owner,
        unlockPrice: formatEther(info.unlockPrice),
        hasPassword: info.hasPassword,
        createdAt: Number(info.createdAt),
        totalUnlocks: Number(info.totalUnlocks),
        hasAccess: info.hasAccess,
      };
    } catch (err) {
      console.error('Failed to get page info:', err);
      return null;
    }
  };

  return {
    createPage,
    unlockWithPayment,
    unlockWithPassword,
    getPage,
    getUserPages,
    getPageInfo,
    loading,
    error,
  };
}
